import {
  SalesCurrency,
  SalesModeOfPayment,
} from "../../../api/redemption/Sales";

export type { SalesCurrency, SalesModeOfPayment };

export const SALES_AMOUNT_MIN = "0";
export const SALES_AMOUNT_VALIDATION_MESSAGE = (
  "Amount Paid must be zero or greater with no more than 2 decimal places"
);

type SalesPaymentRule = {
  percentageFee: number;
  fixedFee: number;
  withholdingTaxRate: number;
};

export type SalesPaymentCalculation = SalesPaymentRule & {
  symbol: string;
  processingFee: number;
  withholdingTaxBasis: number;
  creditableWithholdingTax: number;
  totalDeductions: number;
  netAmountReceived: number;
};

export const SALES_PAYMENT_METHODS: Record<
  SalesCurrency,
  SalesModeOfPayment[]
> = {
  PHP: ["GCash Business", "Bank Transfer", "Cash"],
  SGD: ["PayNow / PayLah!", "Card Payment", "Cash"],
};

const SALES_PAYMENT_RULES: Record<
  SalesCurrency,
  Partial<Record<SalesModeOfPayment, SalesPaymentRule>>
> = {
  PHP: {
    "GCash Business": {
      percentageFee: 1,
      fixedFee: 0,
      withholdingTaxRate: 0.5,
    },
    "Bank Transfer": {
      percentageFee: 0,
      fixedFee: 0,
      withholdingTaxRate: 0,
    },
    Cash: {
      percentageFee: 0,
      fixedFee: 0,
      withholdingTaxRate: 0,
    },
  },
  SGD: {
    "PayNow / PayLah!": {
      percentageFee: 0,
      fixedFee: 0,
      withholdingTaxRate: 0,
    },
    "Card Payment": {
      percentageFee: 3.2,
      fixedFee: 0.4,
      withholdingTaxRate: 0,
    },
    Cash: {
      percentageFee: 0,
      fixedFee: 0,
      withholdingTaxRate: 0,
    },
  },
};

export const currencySymbol = (currency: SalesCurrency): string => (
  currency === "PHP" ? "₱" : "S$"
);

export const roundSalesMoney = (value: number): number => (
  Math.round((value + Number.EPSILON) * 100) / 100
);

export function calculateSalesPayment(
  currency: SalesCurrency,
  modeOfPayment: SalesModeOfPayment,
  amountPaid: number,
): SalesPaymentCalculation {
  const rule = SALES_PAYMENT_RULES[currency][modeOfPayment];
  if (!rule) {
    throw new Error("Mode of Payment is not valid for the selected Currency");
  }

  const processingFee = roundSalesMoney(
    (amountPaid * rule.percentageFee / 100) + rule.fixedFee,
  );
  const withholdingTaxBasis = modeOfPayment === "GCash Business"
    ? (amountPaid / 1.12) - processingFee
    : 0;
  const creditableWithholdingTax = modeOfPayment === "GCash Business"
    ? roundSalesMoney(withholdingTaxBasis * rule.withholdingTaxRate / 100)
    : 0;
  const totalDeductions = roundSalesMoney(
    processingFee + creditableWithholdingTax,
  );

  return {
    ...rule,
    symbol: currencySymbol(currency),
    processingFee,
    withholdingTaxBasis,
    creditableWithholdingTax,
    totalDeductions,
    netAmountReceived: roundSalesMoney(amountPaid - totalDeductions),
  };
}

export function isValidSalesAmount(value: unknown): boolean {
  if (typeof value !== "string" && typeof value !== "number") {
    return false;
  }
  const text = String(value);
  return /^\d+(\.\d{1,2})?$/.test(text) && Number(text) >= 0;
}
